
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { emptyToLine, splitThousands } from "@common/src/utils/common";
import BtnCheckGroup from "@common/src/components/scope/btn-check-group/index.vue";
import GeneralVehicleSelect from "@common/src/components/scope/vehicle-select/index.vue";
import dayjs from "dayjs";
import { priceRulesEl, partsQuantityRules } from "@common/src/utils/verify";
import { addCurrencyUnit } from "@common/src/utils/tools";
import t from "@common/src/i18n/t";
import MyCheckbox from "@common/src/components/scope/my-checkbox/index.vue";
import { InjectFunction } from "@common/src/utils/decorator";

const parts = namespace("parts");
const user = namespace("user");
const app = namespace("app");
const base = namespace("base");
const inventory = namespace("inventory");
const serviceItem = namespace("service-item");
const common_base = namespace("common_base");

@Component({
  components: { BtnCheckGroup, GeneralVehicleSelect, MyCheckbox }
})
@InjectFunction("permitApp", "appCode")
export default class ServiceItemEdit extends Vue {
  @app.Mutation setPBPX;
  @serviceItem.Action createServiceItem;
  @serviceItem.Action updateService;
  @serviceItem.Action getServiceDetail;
  @serviceItem.Action queryTwoLevelClassList;
  @parts.Action getPartsList;
  @parts.Action getPartsDetail;
  @user.State userData;
  @base.Action getDictionaryList;
  @inventory.Action saveInWarehouse;
  @app.State btnLoading;
  @app.Mutation btnStartLoading;
  @app.Mutation btnStopLoading;
  @common_base.State showPlatformParts;
  @base.Mutation checkPageHasInstruction;
  dayjs = dayjs;
  splitThousands = splitThousands;
  addCurrencyUnit = addCurrencyUnit;
  orderFormData: any = {
    serviceType: "1", // 工时or套餐
    serviceCategoryCode: "", // 二级分类
    serviceName: "", // 项目名称
    vehicleSeries: [], // 适用车系
    workingHours: "", // 工时
    serviceFee: "", // 工时费
    status: "1", // 是否启用
    inboundType: "",
    servicePartsList: [],
    costAmount: "0",
    storeTypes: [] //门店类型
  };
  get isSystemSet() {
    return this.orderFormData.source === "1";
  }
  typeOptionsData: any = [
    {
      label: t("setting.working-hours-project"),
      value: "1"
    },
    {
      label: t("setting.plan-project"),
      value: "2"
    }
  ];
  get breadData() {
    return [
      { name: t("v213.Service"), path: "/setting/service" },
      {
        name: this.isAdd ? t("setting.add-service-items") : t("setting.editing-service-items")
      }
    ];
  }
  get isAdd() {
    return this.$route.name === "settingServiceAdd";
  }
  orderFormDataRules = {
    serviceCategoryCode: [{ required: true, message: t("setting.select-secondary-category"), trigger: "change" }],
    serviceName: [{ required: true, message: t("setting.enter-project-name"), trigger: "blur" }],
    serviceFee: [
      { required: true, message: t("setting.enter-hourly-rate-package-rate"), trigger: "blur" },
      {
        validator: this.checkPrice,
        trigger: "blur"
      }
    ],
    status: [{ required: true, message: t("setting.select-whether-enable"), trigger: "blur" }],
    storeTypes: [{ required: true, message: t("v210831.please-select"), trigger: "blur" }]
  };

  defaultVehicleList: any = [];
  checkPrice(rule, value, callback) {
    if (!value) {
      callback();
    } else {
      priceRulesEl(value, callback);
    }
  }
  emptyToLine = emptyToLine;

  definitionPartsData: any = {
    partsUnit: "", // 单位
    sellingPrice: "", // 销售价格
    amount: "", // 账面金额
    servicePartsQty: "", // 数量
    partsCode: "" // 配件code
  };
  twoLevelClassList: any = [];
  partsList: any[] = [];
  get orderId() {
    return this.$route.params.id;
  }
  created() {
    this.setPBPX(52);
    this.init();
  }
  handleServiceTypeChange() {
    this.getTwoLevelClassList("");
  }
  serviceTypeList: any = [];
  twoLevelClassListCopy: any = [];
  handleSearchTwoClass(val) {
    if (val) {
      this.twoClassSearchValue = val;
      this.twoLevelClassList = this.twoLevelClassList.filter(item => {
        return item.serviceCategoryName.includes(val);
      });
    } else {
      this.twoLevelClassList = this.twoLevelClassListCopy;
    }
  }
  handleAddParts() {
    if (
      this.appCode === "speedbiz-operation" &&
      (!this.orderFormData.storeTypes || this.orderFormData.storeTypes.length < 1)
    ) {
      return this.$message.error(t("v210831.please-select-the-store-type"));
    }
    this.orderFormData.servicePartsList.push(Object.assign({}, this.definitionPartsData));
  }
  twoClassSearchValue: string = ""; // 品牌搜索keyword
  handleServiceClassChange() {
    this.getTwoLevelClassList("");
    this.orderFormData.serviceCategoryCode = "";
  }
  getTwoLevelClassList(val = "", callback?) {
    this.queryTwoLevelClassList({
      serviceCategoryName: val,
      serviceClass: "",
      pageSize: 999,
      pageNum: 1
    }).then(data => {
      this.twoLevelClassList = data.data.list;
      this.twoLevelClassListCopy = data.data.list.slice();
      callback && callback();
    });
  }
  storeTypeList: any = [];
  async init() {
    const queryServiceItemName = this.$route.query.serviceItemName;
    if (queryServiceItemName) {
      this.orderFormData.serviceName = queryServiceItemName;
    }
    if (this.permitApp("speedbiz-operation")) {
      this.getDictionaryList("store_type").then(data => {
        this.storeTypeList = data;
      });
    }
    if (this.isAdd) {
      this.getTwoLevelClassList("");
    }
    this.getPartsLists();
    this.getDictionaryList("service_class").then(data => {
      this.serviceTypeList = data;
    });
    if (!this.isAdd) {
      const serviceItemData = await this.getServiceDetail({
        serviceCode: this.$route.params.id
      });
      serviceItemData.data.servicePartsList.forEach(item => {
        if (
          !this.partsList.some(partsItem => {
            return partsItem.partsCode === item.partsCode;
          })
        ) {
          this.partsList.push({
            partsCode: item.partsCode,
            partsName: item.partsName,
            partsNameDesc: item.partsNameDesc
          });
        }
      });
      this.orderFormData = Object.assign(serviceItemData.data);
      this.getTwoLevelClassList("");
      this.defaultVehicleList = this.vehicleDataFormat(serviceItemData.data.vehicleSeries);
    }
  }
  vehicleDataFormat(data) {
    return data.map(item => ({
      lastValue: { brandName: item.brandName, factoryName: "", seriesName: item.seriesName },
      name: item.brandName + " / " + item.seriesName,
      valueGroup: [item.brandName, item.seriesName]
    }));
  }
  submitForm() {
    if (this.orderFormData.serviceType === "1") {
      // 保存的时候如果是工时项目，清空零配件
      this.orderFormData.servicePartsList = [];
    }
    (this.$refs.serviceForm as any).validate(valid => {
      if (valid && this.checkAddData()) {
        if (this.isAdd) {
          this.addService();
        } else {
          this.editService();
        }
      } else {
        console.log("error submit!!");
      }
    });
  }
  addService() {
    this.btnStartLoading();
    this.createServiceItem(this.orderFormData)
      .then(() => {
        this.$message.success(t("base.success") as any);
        this.$router.push(`/setting/service`);
      })
      .finally(() => {
        this.btnStopLoading();
      });
  }
  editService() {
    this.btnStartLoading();
    this.updateService(this.orderFormData)
      .then(() => {
        this.$message.success(t("base.success") as any);
        this.$router.push(`/setting/service`);
      })
      .finally(() => {
        this.btnStopLoading();
      });
  }
  getPartsLists(name = "") {
    this.getPartsList({
      pageNum: 1,
      pageSize: 20,
      searchKey: name,
      ...(this.permitApp("speedbiz")
        ? { notOpadmin: this.showPlatformParts ? "0" : "1" }
        : { storeTypeArray: this.orderFormData.storeTypes })
    }).then(data => {
      this.partsList = (data.data?.list || []).map(item => {
        item.partsNameDesc = item.partsCustomizeCode
          ? `${item.partsNameDesc}(${item.partsCustomizeCode})`
          : item.partsNameDesc;
        return item;
      });
    });
  }

  handleSwitchType(key) {
    this.orderFormData.orderTypeByImmediately = false;
    this.orderFormData.orderTypeByAppointment = false;
    if (key === "orderTypeByImmediately") {
      this.orderFormData.reservationArrivalTime = "";
    } else {
      this.orderFormData.leaveTime = "";
    }
    this.orderFormData[key] = true;
  }
  computedAmount(index) {
    let amount =
      (this.orderFormData.servicePartsList[index].servicePartsQty || 0) *
      this.orderFormData.servicePartsList[index].sellingPrice;
    this.orderFormData.servicePartsList[index].amount = amount.i18nToFixed();
    this.computedTotalAmount();
  }
  computedTotalAmount() {
    const partsTotal = this.orderFormData.servicePartsList.reduce((preV, curV) => preV + Number(curV.amount), 0);
    if (Object.is(NaN, partsTotal)) {
      return this.$message.error(t("setting.enter-quantity-or-amount"));
    }
    console.log(partsTotal);
    this.orderFormData.costAmount = partsTotal.i18nToFixed();
  }
  searchParts(val) {
    this.getPartsLists(val);
  }
  handleChooseParts(val, index) {
    if (!val) return;
    this.getPartsDetail({
      partsCode: val
    }).then(data => {
      let partsData = Object.assign(
        {},
        {
          partsCode: val,
          partsUnit: data.data.partsUnit, // 单位
          sellingPrice: data.data.sellingPrice, // 销售价格
          amount: data.data.amount, // 账面金额
          servicePartsQty: data.data.servicePartsQty // 数量
        }
      );
      this.orderFormData.servicePartsList.splice(index, 1, partsData);
      this.computedAmount(index);
    });
  }
  handlePartsClear(index) {
    this.orderFormData.servicePartsList.splice(index, 1, Object.assign({}, this.definitionPartsData));
  }
  checkAddData(): boolean {
    if (this.orderFormData.serviceType === "2" && this.orderFormData.servicePartsList.length < 1) {
      this.$message.error(t("setting.add-least-one-component"));
      return false;
    }
    for (let index = 0; index < this.orderFormData.servicePartsList.length; index++) {
      const element = this.orderFormData.servicePartsList[index];
      if (!element.partsCode) {
        this.$message.error(t("setting.select-associated-parts"));
        return false;
      }
      if (!element.servicePartsQty) {
        this.$message.error(t("setting.fill-quantity-required"));
        return false;
      }
      const checkPartsQty = partsQuantityRules(element.servicePartsQty, false);
      if (!checkPartsQty.success) {
        this.$message.error(checkPartsQty.msg);
        return false;
      }
    }
    return true;
  }

  deleteItem(key, index) {
    console.log(key, index);
    this.orderFormData[key].splice(index, 1);
  }
  handleOpPartsShowChange() {
    this.getPartsLists();
  }
  @Watch("orderFormData.serviceType", { immediate: true })
  serviceTypeChange(val) {
    if (this.permitApp("speedbiz")) {
      if (val === "1") {
        this.checkPageHasInstruction({
          resourceCode: "PC_SERVICEITEM_NEW"
        });
      } else if (val === "2") {
        this.checkPageHasInstruction({
          resourceCode: "PC_SERVICEITEM_SET_NEW"
        });
      }
    }
  }
  handleStoreTypeChange() {
    this.getPartsLists();
    this.orderFormData.servicePartsList = [];
  }
}
